<script setup>
import ModalBox from '@/components/modals/ModalBox.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { computed, ref, watch, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useNotification } from '@kyvg/vue3-notification';
import _ from 'lodash';
import { Duration } from 'luxon';
import StreamService from '@/services/StreamService';
import MetricsService from '@/services/MetricsService';
import UserService from '@/services/UserService';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import Spinner from '@/components/ui/Spinner.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue';
import IconSearch from '@/components/icon/IconSearch.vue';
import IconFolderFilled from '@/components/icon/IconFolderFilled.vue';
import IconCross from '@/components/icon/IconCross.vue';
import IconCheckmarkStroke from '@/components/icon/IconCheckmarkStroke.vue';
import { track, EVENTS } from '@/lib/tracking';

const { isVisible, hide: hideLegacyModal } = useLegacyModalBridge('modal-add-recorded-videos')
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const store = useStore();
const { notify } = useNotification();

const props = defineProps({
	stream: { type: Object, required: true },
	totalFilesPerStream: { type: Number, default: 999 },
	videoFiles: { type: Array, default: () => [] },
	visible: { type: Boolean, default: false },
});

const emit = defineEmits(['updated', 'close']);

const selectedFolder = ref(null);
const streams = ref([]);
const searchString = ref('');
const selectedFiles = ref(new Map()); // key: file.id || file.fileName, value: file object — persisted across folder navigation
const isLoaded = ref(false);
const isLoading = ref(false);
const isPlaylistLoaded = ref(false);
const scheduledStorageLimit = ref({});
const activeScheduledSub = ref({});
const folderCurrentPage = ref(1);
const fileCurrentPage = ref(1);
const itemsPerPage = ref(6);

// Computed
const allStreams = computed(() => store.getters['Streams/streams']);
const activeTeam = computed(() => store.state.User.activatedTeam);

const maxVideoFileSize = computed(() => {
	let maxSize = null;
	const subscriptions = store.state.User.subscriptions;
	const activeScheduledSubs = _.filter(subscriptions, (sub) => sub.category === 'scheduled' && sub.enabled === true);
	activeScheduledSubs.forEach(sub => {
		try {
			let override = parseFloat(sub.package.definition.schedulerMaxVideoSize);
			if (sub.definitionOverride && sub.definitionOverride.schedulerMaxVideoSize) {
				override = parseFloat(sub.definitionOverride.schedulerMaxVideoSize);
			}
			if (maxSize) {
				if (override > maxSize) { maxSize = override; activeScheduledSub.value = sub; }
			} else {
				maxSize = override; activeScheduledSub.value = sub;
			}
		} catch (e) { console.error(e); }
	});
	if (scheduledStorageLimit.value?.unitsLeft > 0 && scheduledStorageLimit.value.unitsLeft < maxSize) {
		maxSize = parseFloat(scheduledStorageLimit.value.unitsLeft / (1000 ** 3)).toFixed(1);
	}
	if (scheduledStorageLimit.value?.unitsLeft <= 0) maxSize = 0;
	return maxSize;
});

const filteredFolders = computed(() => {
	const filtered = _.orderBy(streams.value, ['creationTime'], ['desc']).filter(s => s.type === 'vod' && s.liveParent);
	return filtered.filter(s => s.name.toLowerCase().includes(searchString.value.toLowerCase()));
});

const totalFolderPages = computed(() => Math.max(1, Math.ceil(filteredFolders.value.length / itemsPerPage.value)));
const paginatedFolders = computed(() => {
	const start = (folderCurrentPage.value - 1) * itemsPerPage.value;
	return filteredFolders.value.slice(start, start + itemsPerPage.value);
});

const filteredFiles = computed(() => {
	if (!selectedFolder.value?.playlist) return [];
	const q = searchString.value.toLowerCase();
	if (!q) return selectedFolder.value.playlist;
	return selectedFolder.value.playlist.filter(f => (f.fileName || '').toLowerCase().includes(q));
});
const totalFilePages = computed(() => {
	return Math.max(1, Math.ceil(filteredFiles.value.length / itemsPerPage.value));
});
const paginatedFiles = computed(() => {
	const start = (fileCurrentPage.value - 1) * itemsPerPage.value;
	return filteredFiles.value.slice(start, start + itemsPerPage.value);
});

const totalFilesSelected = computed(() => selectedFiles.value.size);

const videoSelected = computed(() => Array.from(selectedFiles.value.values()));

const maxReached = computed(() => {
	return totalFilesSelected.value + props.videoFiles.length > props.totalFilesPerStream;
});

function getFileKey(item) {
	return item.id || item.fileName;
}

const maxCanSelect = computed(() => {
	return props.totalFilesPerStream - props.videoFiles.length;
});

// Pagination helpers
const folderPages = computed(() => buildPages(folderCurrentPage.value, totalFolderPages.value));
const filePages = computed(() => buildPages(fileCurrentPage.value, totalFilePages.value));

function buildPages(current, total) {
	if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
	const pages = [1];
	if (current > 3) pages.push('...');
	for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
	if (current < total - 2) pages.push('...');
	pages.push(total);
	return pages;
}

// Methods
function resetState() {
	selectedFolder.value = null;
	searchString.value = '';
	selectedFiles.value = new Map();
	folderCurrentPage.value = 1;
	fileCurrentPage.value = 1;
	isPlaylistLoaded.value = false;
}

function show() {
	resetState();
}

function hide() {
	hideLegacyModal();
	selectedFolder.value = null;
	searchString.value = '';
	selectedFiles.value = new Map();
}

function videoValidate(video) {
	return (video.bytes <= parseFloat(maxVideoFileSize.value) * (1000 ** 3) && video.bytes <= scheduledStorageLimit.value.unitsLeft && scheduledStorageLimit.value.unitsLeft > 0);
}

function getErrorMessage(video) {
	if (video.bytes > parseFloat(maxVideoFileSize.value) * (1000 ** 3)) return 'Only file under ' + maxVideoFileSize.value + 'GB are allowed.';
	if (video.bytes > scheduledStorageLimit.value.unitsLeft || scheduledStorageLimit.value.unitsLeft <= 0) return 'Storage limit exceeded. Please upgrade.';
	return '';
}

async function openFolder(folder) {
	selectedFolder.value = _.cloneDeep(folder);
	isPlaylistLoaded.value = false;
	fileCurrentPage.value = 1;
	searchString.value = '';
	const res = await StreamService.getStreamPlaylist(selectedFolder.value._id);
	selectedFolder.value.playlist = res.map(item => {
		const key = item.id || item.fileName;
		return {
			...item,
			liveToVod: selectedFolder.value.liveParent ? true : false,
			selected: selectedFiles.value.has(key),
			invalid: !videoValidate(item),
			errorMessage: !videoValidate(item) ? getErrorMessage(item) : '',
		};
	});
	isPlaylistLoaded.value = true;
}

function goBackToFolders() {
	selectedFolder.value = null;
	isPlaylistLoaded.value = false;
	fileCurrentPage.value = 1;
	searchString.value = '';
}

function toggleFile(item) {
	const key = getFileKey(item);
	if (selectedFiles.value.has(key)) {
		selectedFiles.value.delete(key);
		item.selected = false;
	} else {
		if (maxReached.value) return;
		selectedFiles.value.set(key, item);
		item.selected = true;
	}
	// Trigger reactivity for Map mutations
	selectedFiles.value = new Map(selectedFiles.value);
}

async function onConfirm() {
	try {
		isLoading.value = true;
		const streamObj = props.stream;
		const addedCount = videoSelected.value.length;
		for (const video of videoSelected.value) {
			let videoId;
			let importedVideoId;
			if (video.liveToVod) {
				const videoUrl = video.downloadUrl;
				const fileName = videoUrl.substring(videoUrl.indexOf(streamObj.user));
				const prefix = (video.archive ? video.archive.abr : video.abr) ? 'edge-en' : 'edge';
				videoId = `episodes/${prefix}/${fileName}`;
			} else {
				importedVideoId = video.srcHls.substring(video.srcHls.indexOf('videos/'), video.srcHls.indexOf('/index.m3u8'));
				videoId = video.id;
			}
			const payload = {
				id: videoId,
				fileName: video.fileName + '.mp4',
				type: video.liveToVod ? 'live-to-vod' : 'imported',
				bytes: video.bytes,
				mediaInfo: {
					codecs: [{ type: 'video', codec: 'AVC' }, { type: 'audio', codec: 'AAC' }],
					duration: video.mediaInfo.duration,
					durationMins: video.mediaInfo.durationMins,
				},
			};
			if (payload.type === 'imported') payload.importedVideoId = importedVideoId;
			await StreamService.saveStreamPlaylistVideo(streamObj._id, payload);
		}
		await store.dispatch('Streams/getStreams');
		track(EVENTS.VOD_ADDED_TO_STREAM, { count: addedCount, streamId: streamObj._id });
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Failed to add videos' });
	}
	setTimeout(() => {
		hide();
		emit('updated');
		isLoading.value = false;
	}, 1000);
}

function getBytes(size) {
	if (size <= 0) return '0';
	const i = Math.floor(Math.log(size) / Math.log(1000));
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
	return (size / Math.pow(1000, i)).toFixed(2) * 1 + ' ' + sizes[i];
}

function formatDuration(seconds) {
	if (!seconds) return '-';
	let fmt = "s's'";
	if (seconds >= 60) fmt = "m'm' s's'";
	if (seconds >= 3600) fmt = "h'h' " + fmt;
	return Duration.fromObject({ seconds }).toFormat(fmt);
}

function formatDate(ts) {
	if (!ts) return '-';
	const d = new Date(ts);
	const pad = (n) => String(n).padStart(2, '0');
	return `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

// Expose for parent ref access
// Watch for open to reset state
watch(() => props.visible, (val) => { if (val) resetState(); });

// Reset pagination when the user types in the search box so filtered
// results don't leave the view stuck on an empty trailing page.
watch(searchString, () => {
	folderCurrentPage.value = 1;
	fileCurrentPage.value = 1;
});

// Lifecycle
onMounted(async () => {
	try {
		const subscriptions = store.state.User.subscriptions;
		const activeScheduledSubs = _.filter(subscriptions, (sub) => sub.category === 'scheduled' && sub.enabled === true);
		activeScheduledSubs.forEach(sub => {
			try {
				const size = parseFloat(sub.package.definition.schedulerMaxVideoSize);
				if (!activeScheduledSub.value?.package || size > parseFloat(activeScheduledSub.value.package.definition.schedulerMaxVideoSize)) {
					activeScheduledSub.value = sub;
				}
			} catch (e) { console.error(e); }
		});
		const userId = activeTeam.value ? UserService.getUserId(activeTeam.value) : store.state.User?._id;
		const res = await MetricsService.getSubscriptionStorage(userId, activeScheduledSub.value?.package?._id);
		scheduledStorageLimit.value = res || {};
		streams.value = allStreams.value || [];
		isLoaded.value = true;
	} catch (e) {
		console.error('AddRecordedVideosModal mount error:', e);
		streams.value = allStreams.value || [];
		isLoaded.value = true;
	}
});

onUnmounted(() => {});
</script>

<template>
	<ModalBox v-model="isVisible" size="lg" scroll-body mobile-placement="bottom" hide-close header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none p-0">

		<div class="flex flex-col h-full">
					<!-- Header -->
					<div class="px-6 pt-5 pb-4 shrink-0">
						<template v-if="!selectedFolder">
							<div class="flex items-start justify-between gap-3 mb-0.5">
								<h2 class="text-lg font-semibold text-white m-0">Add recorded videos</h2>
								<FormButton @click="hide" custom-classes="text-dark-8 hover:text-white p-1 -mr-1 -mt-1 shrink-0">
									<IconBase fill="none" width="14" height="14">
										<IconCross />
									</IconBase>
								</FormButton>
							</div>
							<p class="text-sm text-dark-8 mb-4">Select the file you'd like to upload.</p>
						<div class="relative">
							<FormInput v-model="searchString" placeholder="Search..."
								custom-classes="!bg-dark-3 !border-0 !rounded-lg !pr-10" />
							<IconBase class="absolute right-3 top-1/2 -translate-y-1/2 text-dark-7" width-view-box="16" height-view-box="16" width="16" height="16">
								<IconSearch />
							</IconBase>
						</div>
					</template>
					<template v-else>
							<div class="flex items-center justify-between gap-2">
								<div class="flex items-center gap-2 min-w-0">
									<FormButton @click="goBackToFolders" custom-classes="text-dark-8 hover:text-white p-1 shrink-0">
										<IconBase fill="none" width="14" height="14">
											<IconChevronLeft />
										</IconBase>
									</FormButton>
									<h2 class="text-lg font-semibold text-white m-0 truncate">{{ selectedFolder.name }}</h2>
								</div>
								<FormButton @click="hide" custom-classes="text-dark-8 hover:text-white p-1 -mr-1 shrink-0">
									<IconBase fill="none" width="14" height="14">
										<IconCross />
									</IconBase>
								</FormButton>
							</div>
						<div class="relative mt-3">
							<FormInput v-model="searchString" placeholder="Search..."
								custom-classes="!bg-dark-3 !border-0 !rounded-lg !pr-10" />
							<IconBase class="absolute right-3 top-1/2 -translate-y-1/2 text-dark-7" width-view-box="16" height-view-box="16" width="16" height="16">
								<IconSearch />
							</IconBase>
						</div>
						</template>
					</div>

					<!-- Body -->
					<div class="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
						<!-- Folder list -->
						<template v-if="!selectedFolder && isLoaded">
							<!-- Column headers (desktop only) -->
							<div class="hidden md:grid grid-cols-[1fr_50px_150px] gap-x-3 px-3 pb-2 text-xs text-dark-7 font-medium">
								<span>Name</span>
								<span>Files</span>
								<span>Date</span>
							</div>
							<div class="md:hidden px-3 pb-2 text-xs text-dark-7 font-medium">
								<span>Name</span>
							</div>
							<!-- Rows -->
							<div class="flex flex-col gap-y-1.5">
								<div v-for="folder in paginatedFolders" :key="folder._id"
									@click="openFolder(folder)"
									class="flex md:grid md:grid-cols-[1fr_50px_150px] gap-x-3 md:items-center px-3 py-2 bg-dark-3 border border-dark-4 rounded-lg cursor-pointer hover:border-dark-5 transition-colors">
									<div class="flex items-center gap-2.5 min-w-0 flex-1">
										<IconBase class="text-dark-7 shrink-0" :width="16" :height="16">
											<IconFolderFilled />
										</IconBase>
										<div class="flex flex-col min-w-0 flex-1">
											<span class="text-sm text-white truncate">{{ folder.name }}</span>
											<span class="md:hidden text-xs text-dark-7 truncate">{{ folder.totalFiles }} files</span>
											<span class="md:hidden text-xs text-dark-7 truncate">{{ formatDate(folder.creationTime) }}</span>
										</div>
									</div>
									<span class="hidden md:inline text-sm text-dark-9">{{ folder.totalFiles }}</span>
									<span class="hidden md:inline text-sm text-dark-9">{{ formatDate(folder.creationTime) }}</span>
								</div>
							</div>
							<!-- Pagination + selection count -->
							<div class="mt-4 mb-2">
								<div v-if="totalFolderPages > 1" class="flex items-center justify-start gap-x-1 mb-2">
									<FormButton custom-classes="px-2 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="folderCurrentPage <= 1" @click="folderCurrentPage = 1">&laquo;</FormButton>
									<FormButton custom-classes="px-1.5 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="folderCurrentPage <= 1" @click="folderCurrentPage--">&lsaquo;</FormButton>
									<template v-for="p in folderPages" :key="'fp-'+p">
										<span v-if="p === '...'" class="px-1 text-xs text-dark-7">...</span>
										<FormButton v-else @click="folderCurrentPage = p"
											:custom-classes="(p === folderCurrentPage ? 'bg-surface-4 text-white' : 'text-dark-8 hover:text-white') + ' px-2 py-0.5 text-xs rounded'">{{ p }}</FormButton>
									</template>
									<FormButton custom-classes="px-1.5 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="folderCurrentPage >= totalFolderPages" @click="folderCurrentPage++">&rsaquo;</FormButton>
									<FormButton custom-classes="px-2 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="folderCurrentPage >= totalFolderPages" @click="folderCurrentPage = totalFolderPages">&raquo;</FormButton>
								</div>
								<div class="text-sm text-dark-8">
									<span :class="maxReached ? 'text-error' : ''">{{ totalFilesSelected }}</span> {{ totalFilesSelected === 1 ? 'file' : 'files' }} selected<template v-if="totalFilesSelected > 0">. Maximum {{ props.totalFilesPerStream }} can be selected. <router-link class="text-dark-9 underline" to="/subscribe?category=live" target="_blank">Upgrade here</router-link></template>
								</div>
							</div>
						</template>

						<!-- File list -->
						<template v-else-if="selectedFolder && isPlaylistLoaded">
							<div class="hidden md:grid grid-cols-[1fr_80px_80px_150px] gap-x-3 px-3 pb-2 text-xs text-dark-7 font-medium">
								<span>Name</span>
								<span>Size</span>
								<span>Duration</span>
								<span>Date</span>
							</div>
							<div class="md:hidden px-3 pb-2 text-xs text-dark-7 font-medium">
								<span>Name</span>
							</div>
							<div class="flex flex-col gap-y-1.5">
								<div v-for="file in paginatedFiles" :key="file.id || file.fileName"
									@click="!file.invalid && toggleFile(file)"
									:class="[
										'flex md:grid md:grid-cols-[1fr_80px_80px_150px] gap-x-3 md:items-center px-3 py-2 rounded-lg border transition-colors',
										'bg-dark-3 border-dark-4',
										file.invalid ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-dark-5',
									]"
									:style="file.selected ? 'background-color: rgba(0, 98, 255, 0.15) !important; border-color: #0062FF !important;' : ''">
									<div class="flex items-center gap-2.5 min-w-0 flex-1">
										<div class="shrink-0 w-[18px] h-[18px] rounded-[4px] border-2 flex items-center justify-center transition-colors"
											:class="file.selected ? 'border-[#4B8FF7] bg-[#4B8FF7]' : 'border-dark-7 bg-transparent'">
											<IconBase v-if="file.selected" class="text-white block" width="11" height="11" fill="none">
												<IconCheckmarkStroke />
											</IconBase>
										</div>
										<div class="flex flex-col min-w-0 flex-1">
											<span class="text-sm text-white truncate">{{ file.fileName }}</span>
											<span class="md:hidden text-xs text-dark-7 truncate">{{ getBytes(file.bytes) }} - {{ formatDuration(file.mediaInfo?.duration) }}</span>
											<span class="md:hidden text-xs text-dark-7 truncate">{{ formatDate(file.creationTime) }}</span>
										</div>
									</div>
									<span class="hidden md:inline text-sm text-dark-9">{{ getBytes(file.bytes) }}</span>
									<span class="hidden md:inline text-sm text-dark-9">{{ formatDuration(file.mediaInfo?.duration) }}</span>
									<span class="hidden md:inline text-sm text-dark-9">{{ formatDate(file.creationTime) }}</span>
								</div>
							</div>
							<!-- File pagination + selection count -->
							<div class="mt-4 mb-2">
								<div v-if="totalFilePages > 1" class="flex items-center justify-start gap-x-1 mb-2">
									<FormButton custom-classes="px-2 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="fileCurrentPage <= 1" @click="fileCurrentPage = 1">&laquo;</FormButton>
									<FormButton custom-classes="px-1.5 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="fileCurrentPage <= 1" @click="fileCurrentPage--">&lsaquo;</FormButton>
									<template v-for="p in filePages" :key="'filep-'+p">
										<span v-if="p === '...'" class="px-1 text-xs text-dark-7">...</span>
										<FormButton v-else @click="fileCurrentPage = p"
											:custom-classes="(p === fileCurrentPage ? 'bg-surface-4 text-white' : 'text-dark-8 hover:text-white') + ' px-2 py-0.5 text-xs rounded'">{{ p }}</FormButton>
									</template>
									<FormButton custom-classes="px-1.5 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="fileCurrentPage >= totalFilePages" @click="fileCurrentPage++">&rsaquo;</FormButton>
									<FormButton custom-classes="px-2 py-1 text-xs text-dark-8 hover:text-white disabled:opacity-30" :disabled="fileCurrentPage >= totalFilePages" @click="fileCurrentPage = totalFilePages">&raquo;</FormButton>
								</div>
								<div class="text-sm text-dark-8">
									<span :class="maxReached ? 'text-error' : ''">{{ totalFilesSelected }}</span> {{ totalFilesSelected === 1 ? 'file' : 'files' }} selected<template v-if="totalFilesSelected > 0">. Maximum {{ props.totalFilesPerStream }} can be selected. <router-link class="text-dark-9 underline" to="/subscribe?category=live" target="_blank">Upgrade here</router-link></template>
								</div>
							</div>
						</template>

						<!-- Loading -->
						<div v-else-if="selectedFolder && !isPlaylistLoaded" class="flex items-center justify-center py-12">
							<Spinner text="Loading files..." classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="15px" />
						</div>
					</div>

					<!-- Footer -->
					<div class="px-6 py-4 border-t border-dark-3 flex items-center justify-end gap-2 shrink-0">
						<FormButton type="secondary" size="md" @click="hide" class="hidden md:inline-flex">Cancel</FormButton>
						<FormButton v-if="!isLoading" type="main" size="md" @click="onConfirm"
							:disabled="totalFilesSelected === 0 || maxReached"
							class="!w-full md:!w-auto !justify-center">Add video</FormButton>
						<FormButton v-else type="main" size="md" disabled
							class="!w-full md:!w-auto !justify-center">
							<Spinner text="Adding..." classes="text-white" spinner-color="white" spinner-size="14px" />
						</FormButton>
					</div>
		</div>
	
</ModalBox>
</template>

<style>
#modal-add-recorded-videos___BV_modal_outer_ {
	z-index: 999999 !important;
}
#modal-add-recorded-videos___BV_modal_outer_ .modal-backdrop {
	z-index: 999998 !important;
}
#modal-add-recorded-videos___BV_modal_outer_ .modal {
	z-index: 999999 !important;
}

/* Ensure modal body lets the inner flex column fill it (so the footer stays inside the sheet) */
#modal-add-recorded-videos___BV_modal_outer_ .modal-body {
	display: flex !important;
	flex-direction: column !important;
	min-height: 0 !important;
	overflow: hidden !important;
}

/* Mobile bottom-sheet drawer behavior */
@media (max-width: 767.98px) {
	#modal-add-recorded-videos___BV_modal_outer_ .modal {
		padding: 0 !important;
	}
	#modal-add-recorded-videos___BV_modal_outer_ .modal-dialog,
	#modal-add-recorded-videos___BV_modal_outer_ .modal-dialog-centered {
		margin: 0 !important;
		max-width: 100% !important;
		width: 100% !important;
		min-height: 100% !important;
		display: flex !important;
		flex-direction: column !important;
		justify-content: flex-end !important;
		align-items: stretch !important;
		pointer-events: none;
	}
	#modal-add-recorded-videos___BV_modal_outer_ .modal-content {
		pointer-events: auto;
		width: 100%;
		max-height: 90vh;
		border-radius: 16px 16px 0 0 !important;
		border-bottom: none !important;
	}
	/* Slide-up animation on mobile (overrides BV's fade) */
	#modal-add-recorded-videos___BV_modal_outer_ .modal.fade .modal-dialog {
		transform: translateY(100%) !important;
		transition: transform 0.3s ease-out !important;
	}
	#modal-add-recorded-videos___BV_modal_outer_ .modal.show .modal-dialog {
		transform: translateY(0) !important;
	}
}
</style>
